import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IClientOrigin } from '@internal-models/client-origin.model';
import { IClient } from '@internal-models/client.model';
import { ClientProvider } from '@internal-providers/client.provider';
import { ClientOriginProvider } from '@internal-providers/client-origin.provider';
import { IFormGroup } from '@shared-libs/interfaces';
import { DebouncedFunc, isEmpty, throttle } from 'lodash';
import { Action, AgeCategory, Gender, NavigationType, UserPermissions } from '@shared-libs/enums';
import { getInvalidFields, translate } from '@shared-libs/helpers';
import { DeviceTypeProvider } from '@internal-providers/device-type.provider';
import { IDeviceType } from '@internal-models/device-type.model';
import { ITargetAudience } from '@internal-models/target-audience.model';
import { TargetAudienceProvider } from '@internal-providers/target-audience.provider';
import { NavigationService } from '@shared-services/navigation.service';
import { BaseComponent } from '@shared-components/base.component';
import { format } from 'date-fns';

@Component({
	selector: 'form-client',
	templateUrl: 'form-client.component.html',
	styleUrls: ['./form-client.component.scss'],
})
export class FormClientMComponent extends BaseComponent implements OnChanges {
	@Input() public formGroup: IFormGroup<Partial<IClient>>;
	@Input() public edit: boolean = false;
	@Input() public isBeegoAppointment: boolean = false;
	@Input() public clientLocked: boolean = false;

	@Output() navigatingToClient: EventEmitter<any> = new EventEmitter();

	public loading: boolean = false;
	public error: boolean = false;
	public gdpr_email_missing: boolean = false;
	public searchResults: Array<IClient> = new Array();
	public currentSearch: object = {};
	private throttledSearch: DebouncedFunc<(scope: FormClientMComponent, search: object) => void>;
	public clientOrigins: Array<IClientOrigin>;
	public deviceTypes: Array<IDeviceType>;
	public genders: Array<{ value: Gender; label: string }>;
	public ageCategories: Array<{ value: AgeCategory; label: string }>;
	public digibankTargetAudiences: Array<ITargetAudience>;
	public invalidFields: Array<any>;
	public errorMessages: Array<any> = new Array();

	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly provider: ClientProvider,
		private readonly clientOriginProvider: ClientOriginProvider,
		private readonly targetAudienceProvider: TargetAudienceProvider,
		private readonly deviceTypeProvider: DeviceTypeProvider,
		private readonly navigationService: NavigationService
	) {
		super();
		this.addResetSearchEventListeners();
		this.getClientOrigins();
		this.getDeviceTypes();
		this.getGenders();
		this.getAgeCategories();
		this.getDigibankTargetAudiences();
	}

	public async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (changes.formGroup?.currentValue) {
			this.validateExistingClient(this.formGroup);
			this.formGroup.get('birthDate').valueChanges.subscribe(() => {
				setTimeout(() => {
					if (this.formGroup.get('birthDate').valid) {
						this.autocomplete({
							birthDate: format(new Date(this.formGroup.get('birthDate').value), 'yyyy-MM-dd'),
						});
					} else {
						this.autocomplete({ birthDate: '' });
					}
				}, 10);
			});
		}
	}

	public autocomplete(search: { [key: string]: string }): void {
		this.setCurrentSearch(search);
		if (search.firstname?.length > 1 || search.lastname?.length > 1 || search.birthDate?.length === 10) {
			this.loading = true;
			if (!this.throttledSearch) {
				this.throttledSearch = throttle(
					(scope: FormClientMComponent, search: object) => {
						scope.provider
							.searchAll(search as any)
							.then((data) => {
								scope.loading = false;
								scope.error = false;
								scope.searchResults = data;
							})
							.catch(() => () => {
								scope.loading = false;
								scope.error = true;
							});
					},
					1000,
					{ trailing: true }
				);
			}
			this.throttledSearch(this, this.currentSearch);
		} else this.resetSearch();
	}

	public selectClient(client: IClient): void {
		this.resetSearch();
		this.currentSearch = {};
		client.birthDate = client.birthDate ? new Date(client.birthDate) : null;
		this.formGroup.patchValue(client);
		this.clientLocked = true;
		this.validateExistingClient(this.formGroup);
	}

	public validateExistingClient(formGroup: IFormGroup<Partial<IClient>>) {
		const address = formGroup.get('Address');
		this.invalidFields = [...getInvalidFields(formGroup), ...getInvalidFields(address)];

		this.errorMessages = this.invalidFields.map((invalidField) => ({
			label: invalidField.field,
			message: this.getErrorMessage(invalidField),
		}));
	}

	public getErrorMessage(invalidField) {
		for (let key in invalidField.errors) {
			return invalidField.errors[key]?.message;
		}
	}

	public async openClient(client?: Partial<IClient>): Promise<void> {
		this.navigatingToClient.emit();

		void this.navigationService.navigateTo<{ action: Action }>(NavigationType.page, '/clienten/client', {
			client,
		});
	}

	public resetSearch(): void {
		this.searchResults = new Array();
	}

	public addResetSearchEventListeners(): void {
		document.addEventListener('keydown', (event: KeyboardEvent) => {
			if (
				event.key === 'Escape' ||
				event.key === 'Esc' ||
				event.keyCode === 27 ||
				event.key === 'Tab' ||
				event.keyCode === 9
			) {
				this.resetSearch();
			}
		});
		document.addEventListener('click', (event) => {
			if ((event.target as HTMLElement).className !== '_search-result') {
				this.resetSearch();
			}
		});
	}

	public gdprClick(e: Event) {
		if (this.formGroup.get('email').valid && this.formGroup.get('email').value) {
			return;
		} else {
			e.preventDefault();
			this.gdpr_email_missing = true;
		}
	}

	private setCurrentSearch(search: { [key: string]: string }): void {
		Object.keys(this.currentSearch).forEach((key) => {
			if (isEmpty(this.currentSearch[key])) {
				delete this.currentSearch[key];
			}
		});
		this.currentSearch = { ...this.currentSearch, ...search };
	}

	private getGenders(): void {
		this.genders = Object.keys(Gender).map((key) => ({
			value: Gender[key],
			label: translate(`CLIENT.GENDER.${key.toUpperCase()}`),
		}));
	}

	private getAgeCategories(): void {
		this.ageCategories = Object.keys(AgeCategory).map((key) => ({
			value: AgeCategory[key],
			label: translate(`CLIENT.AGE_CATEGORY.${AgeCategory[key].toUpperCase().replaceAll('-', '_')}`),
		}));
	}

	private async getDigibankTargetAudiences(): Promise<void> {
		this.digibankTargetAudiences = await this.targetAudienceProvider.getAll();
	}

	private async getClientOrigins(): Promise<void> {
		this.clientOrigins = await this.clientOriginProvider.getAll();
	}

	private async getDeviceTypes(): Promise<void> {
		this.deviceTypes = await this.deviceTypeProvider.getAll();
		this.deviceTypes.push({ name: translate('CLIENT.EXTRA.NO_DEVICE') } as IDeviceType);
	}
}
